import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { Avatar, Box, Button, ButtonBase, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { completedLessonCount, mockCourses } from '../../student/mockData';
import { BLUE, blueButtonSx, cardSx, GREEN, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SURFACE_SOFT } from '../../student/studentStyles';
import { desktopAvatarColors, StudentAvatarColor, studentAvatarColorNames } from '../desktopAvatarColors';
import { StudentDesktopPage } from '../components/StudentDesktopShell';

function StatTile({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
    return (
        <Box sx={{ flex: 1, minWidth: 130, p: 2, borderRadius: '16px', backgroundColor: SURFACE_SOFT }}>
            {icon}
            <Typography sx={{ mt: 0.5, fontFamily: POPPINS, fontWeight: 600, fontSize: 23, color: NAVY }}>{value}</Typography>
            <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>{label}</Typography>
        </Box>
    );
}

interface StudentDesktopProfilePageProps {
    studentName: string;
    avatarColor: StudentAvatarColor;
    onSave: (name: string, color: StudentAvatarColor) => void;
    onNavigate: (page: StudentDesktopPage) => void;
}

export default function StudentDesktopProfilePage({ studentName, avatarColor, onSave, onNavigate }: StudentDesktopProfilePageProps) {
    const [name, setName] = React.useState(studentName);
    const [color, setColor] = React.useState(avatarColor);
    const lessonsDone = mockCourses.reduce((sum, course) => sum + completedLessonCount(course), 0);
    const exercisesDone = mockCourses.reduce((sum, course) => sum + course.lessons.reduce((lessonSum, lesson) => lessonSum + lesson.completedPrompts, 0), 0);

    return (
        <Box sx={{ maxWidth: 1080, mx: 'auto', p: { xs: 2, sm: 3, lg: 4 } }}>
            <Box sx={{ mb: 3 }}>
                <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: { xs: 25, md: 30 }, color: NAVY }}>Profile</Typography>
                <Typography sx={{ mt: 0.4, fontFamily: INTER, fontSize: 14, color: SLATE }}>Your account details and learning progress.</Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, .78fr) minmax(0, 1.5fr)' }, gap: 2.5, alignItems: 'start' }}>
                <Stack spacing={2.5}>
                    <Box sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                        <Avatar sx={{ width: 84, height: 84, mx: 'auto', bgcolor: color, fontFamily: POPPINS, fontWeight: 600, fontSize: 30 }}>{(name || '?').charAt(0).toUpperCase()}</Avatar>
                        <Typography sx={{ mt: 1.75, fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY }}>{name || 'New student'}</Typography>
                        <Typography sx={{ mt: 0.35, fontFamily: INTER, fontSize: 12.5, color: SLATE }}>Learning Chinese</Typography>
                    </Box>

                    <Box sx={{ ...cardSx, p: 2.5 }}>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15, color: NAVY }}>Learning snapshot</Typography>
                        <Stack direction={{ xs: 'column', sm: 'row', md: 'column', lg: 'row' }} spacing={1.25} sx={{ mt: 2 }}>
                            <StatTile icon={<MenuBookRoundedIcon sx={{ color: BLUE, fontSize: 21 }} />} value={lessonsDone} label="lessons done" />
                            <StatTile icon={<CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 21 }} />} value={exercisesDone} label="exercises" />
                        </Stack>
                    </Box>
                </Stack>

                <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3.5 } }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 17, color: NAVY }}>About you</Typography>
                    <Typography sx={{ mt: 0.5, fontFamily: INTER, fontSize: 12.5, color: SLATE }}>This name and avatar appear throughout your student experience.</Typography>

                    <TextField fullWidth label="Your name" value={name} onChange={event => setName(event.target.value)} inputProps={{ maxLength: 40 }} sx={{ mt: 3 }} />
                    <TextField
                        fullWidth
                        label="Email"
                        value="maya.chen@example.com"
                        disabled
                        InputProps={{ startAdornment: <EmailRoundedIcon sx={{ mr: 1, color: SLATE, fontSize: 20 }} /> }}
                        helperText="Sample account address. Account settings are not connected in this preview."
                        sx={{ mt: 2.5 }}
                    />

                    <Typography sx={{ mt: 3, mb: 1.25, fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: SLATE }}>Avatar color</Typography>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                        {desktopAvatarColors.map(option => {
                            const selected = color === option;
                            return (
                                <ButtonBase key={option} aria-label={`${studentAvatarColorNames[option]} avatar color`} aria-pressed={selected} onClick={() => setColor(option)} sx={{ width: 46, height: 46, borderRadius: '50%', bgcolor: option, border: '3px solid #fff', boxShadow: selected ? `0 0 0 2px ${NAVY}` : '0 2px 8px rgba(0,0,0,.12)' }}>
                                    {selected && <CheckRoundedIcon sx={{ color: '#fff', fontSize: 21 }} />}
                                </ButtonBase>
                            );
                        })}
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4, pt: 3, borderTop: `1px solid ${RING_TRACK}` }}>
                        <Button variant="contained" disabled={!name.trim()} onClick={() => onSave(name.trim(), color)} sx={{ ...blueButtonSx, px: 3 }}>Save changes</Button>
                        <Button variant="outlined" startIcon={<SettingsRoundedIcon />} onClick={() => onNavigate('settings')} sx={{ borderColor: RING_TRACK, color: NAVY, fontFamily: INTER, fontWeight: 700, textTransform: 'none', '&:hover': { borderColor: BLUE, backgroundColor: `${BLUE}08` } }}>Lesson settings</Button>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
}
